/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Search, 
  MapPin, 
  Info, 
  X, 
  Check, 
  Plus, 
  Shield, 
  GraduationCap, 
  Building2, 
  UserPlus, 
  Users, 
  Mail, 
  Phone, 
  Star,
  ArrowUpDown,
  Filter,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  FileText,
  RotateCcw,
  AlertCircle,
  ExternalLink,
  Award,
  Sparkles,
  User,
  Briefcase
} from 'lucide-react';
import { StudentAvatar } from '../Common/StudentAvatar';
import { HeroBanner } from '../Common/HeroBanner';
import { FerpaConsentReminderCard } from '../Common/FerpaConsentReminderCard';
import { SearchBar } from '../Common/SearchBar';

export const AdvisorsPage: React.FC = () => {
  const { users, activeUser, activeTenant, updateUserProfile, schools, allSchools, recommendations, setCurrentView, opportunities } = useWBL();
  const [searchQuery, setSearchQuery] = useState('');
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Expanded advisor profile rows
  const [expandedAdvisorIds, setExpandedAdvisorIds] = useState<Record<string, boolean>>({});
  // Expanded recommendation rows (student view)
  const [expandedRecIds, setExpandedRecIds] = useState<Record<string, boolean>>({});
  // Status filter: all | authorized | unassigned
  const [statusFilter, setStatusFilter] = useState<'all' | 'authorized' | 'unassigned'>('all');
  // Sort state
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'status' | 'school'>('name_asc');
  // Status dropdown menu
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Keep track of which school is being selected for which advisor in the dropdown
  const [selectedSchoolForAdvisor, setSelectedSchoolForAdvisor] = useState<Record<string, string>>({});

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users to find all school advisors
  const advisors = useMemo(() => {
    return (users || []).filter(u => u.role === 'advisor');
  }, [users]);

  // Retrieve matching school name for list display
  const resolveSchoolName = (schoolRef: string) => {
    if (!schoolRef) return 'Unspecified School';
    const found = (allSchools || []).find(s => s.id === schoolRef || s.name.toLowerCase() === schoolRef.toLowerCase());
    return found ? found.name : schoolRef;
  };

  // Helper to extract clean list of assigned school references & names for an advisor
  const getAdvisorAssignedSchools = (advisor: any) => {
    const assignedSchoolsMap = new Map<string, string>(); // name -> ref (id preferred)
    
    const refs = [
      advisor.schoolId,
      ...(advisor.schoolsServedIds || []),
      advisor.school,
      ...(advisor.schoolsServed || [])
    ].filter(Boolean) as string[];

    refs.forEach(ref => {
      const name = resolveSchoolName(ref);
      const isId = (allSchools || []).some(s => s.id === ref);
      if (!assignedSchoolsMap.has(name) || isId) {
        assignedSchoolsMap.set(name, ref);
      }
    });

    return Array.from(assignedSchoolsMap.entries()).map(([name, ref]) => ({ name, ref }));
  };

  // Counts for status filter funnel
  const advisorCounts = useMemo(() => {
    let authorized = 0;
    let unassigned = 0;
    advisors.forEach(a => {
      const assigned = getAdvisorAssignedSchools(a);
      if (assigned.length > 0) {
        authorized++;
      } else {
        unassigned++;
      }
    });
    return {
      total: advisors.length,
      authorized,
      unassigned,
    };
  }, [advisors, allSchools]);

  // Apply search query, status filter, and sorting
  const filteredAdvisors = useMemo(() => {
    let list = advisors.filter(advisor => {
      const nameMatch = (advisor.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (`${advisor.contactFirstName || ''} ${advisor.contactLastName || ''}`).toLowerCase().includes(searchQuery.toLowerCase());
      const emailMatch = (advisor.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const assignedSchools = getAdvisorAssignedSchools(advisor);
      const schoolMatch = assignedSchools.some(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesSearch = !searchQuery.trim() || nameMatch || emailMatch || schoolMatch;
      if (!matchesSearch) return false;

      if (statusFilter === 'authorized') {
        return assignedSchools.length > 0;
      }
      if (statusFilter === 'unassigned') {
        return assignedSchools.length === 0;
      }
      return true;
    });

    // Apply sorting
    return [...list].sort((a, b) => {
      if (sortBy === 'name_asc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'name_desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      if (sortBy === 'status') {
        const aAuth = getAdvisorAssignedSchools(a).length > 0 ? 1 : 0;
        const bAuth = getAdvisorAssignedSchools(b).length > 0 ? 1 : 0;
        return bAuth - aAuth;
      }
      if (sortBy === 'school') {
        const aSchool = getAdvisorAssignedSchools(a)[0]?.name || '';
        const bSchool = getAdvisorAssignedSchools(b)[0]?.name || '';
        return aSchool.localeCompare(bSchool);
      }
      return 0;
    });
  }, [advisors, searchQuery, statusFilter, sortBy, allSchools]);

  const toggleAdvisorDetails = (advisorUid: string) => {
    setExpandedAdvisorIds(prev => ({
      ...prev,
      [advisorUid]: !prev[advisorUid]
    }));
  };

  // Attach school to an advisor
  const handleAttachSchool = async (advisorUid: string) => {
    const schoolId = selectedSchoolForAdvisor[advisorUid];
    if (!schoolId) {
      setErrorMsg('Please select a school to attach.');
      setTimeout(() => setErrorMsg(null), 3000);
      return;
    }

    const advisor = advisors.find(u => u.uid === advisorUid);
    if (!advisor) return;

    const currentSchoolsServedIds = advisor.schoolsServedIds || [];
    const currentSchoolsServed = advisor.schoolsServed || [];
    const currentSchoolId = advisor.schoolId;
    const currentSchool = advisor.school;
    
    const foundSchool = (schools || []).find(s => s.id === schoolId);
    const schoolName = foundSchool?.name || schoolId;
    
    // Check if already assigned (by ID or Name)
    const alreadyAssigned = 
      currentSchoolId === schoolId || 
      currentSchoolsServedIds.includes(schoolId) ||
      currentSchool === schoolName ||
      currentSchoolsServed.includes(schoolName);

    if (alreadyAssigned) {
      setErrorMsg('This school is already assigned to the advisor.');
      setTimeout(() => setErrorMsg(null), 3000);
      return;
    }

    try {
      if (!currentSchoolId && !currentSchool) {
        // Assign as primary if none exists
        await updateUserProfile(advisorUid, {
          schoolId: schoolId,
          school: schoolName
        });
      } else {
        // Add to served list
        await updateUserProfile(advisorUid, {
          schoolsServedIds: [...currentSchoolsServedIds, schoolId],
          schoolsServed: [...currentSchoolsServed, schoolName]
        });
      }
      
      setSaveSuccess(`Attached school to ${advisor.name || 'advisor'} successfully.`);
      setTimeout(() => setSaveSuccess(null), 3000);
      
      // Reset selection for this advisor
      setSelectedSchoolForAdvisor(prev => {
        const copy = { ...prev };
        delete copy[advisorUid];
        return copy;
      });
    } catch (e: any) {
      console.error("Failed to attach school:", e);
      setErrorMsg(e.message || 'Error occurred while attaching school.');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  // Detach school from an advisor
  const handleDetachSchool = async (advisorUid: string, schoolRef: string) => {
    const advisor = advisors.find(u => u.uid === advisorUid);
    if (!advisor) return;

    const resolvedName = resolveSchoolName(schoolRef);
    const foundSchool = (schools || []).find(s => s.id === schoolRef || s.name.toLowerCase() === schoolRef.toLowerCase());
    const targetId = foundSchool?.id || schoolRef;
    const targetName = foundSchool?.name || schoolRef;

    // Filter out from served lists
    let updatedServedIds = (advisor.schoolsServedIds || []).filter(id => id !== targetId && id !== targetName);
    let updatedServedNames = (advisor.schoolsServed || []).filter(n => n !== targetId && n !== targetName);
    
    let updatedPrimaryId = advisor.schoolId;
    let updatedPrimaryName = advisor.school;

    // If detaching primary school
    const isPrimaryId = advisor.schoolId && targetId && advisor.schoolId === targetId;
    const isPrimaryName = advisor.school && targetName && advisor.school === targetName;

    if (isPrimaryId || isPrimaryName) {
      if (updatedServedIds.length > 0) {
        // Promote from served ID list
        updatedPrimaryId = updatedServedIds[0];
        updatedServedIds = updatedServedIds.slice(1);
        const promoted = (schools || []).find(s => s.id === updatedPrimaryId);
        updatedPrimaryName = promoted?.name || updatedPrimaryId;
        // Also remove from names if present
        updatedServedNames = updatedServedNames.filter(n => n !== updatedPrimaryName);
      } else if (updatedServedNames.length > 0) {
        // Promote from served Name list
        updatedPrimaryName = updatedServedNames[0];
        updatedServedNames = updatedServedNames.slice(1);
        const promoted = (schools || []).find(s => s.name === updatedPrimaryName);
        updatedPrimaryId = promoted?.id || '';
      } else {
        updatedPrimaryId = '';
        updatedPrimaryName = '';
      }
    }

    try {
      await updateUserProfile(advisorUid, {
        schoolId: updatedPrimaryId,
        school: updatedPrimaryName,
        schoolsServedIds: updatedServedIds,
        schoolsServed: updatedServedNames
      });
      setSaveSuccess(`Detached ${resolvedName} from ${advisor.name || 'advisor'} successfully.`);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (e: any) {
      console.error("Failed to detach school:", e);
      setErrorMsg(e.message || 'Error occurred while detaching school.');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };


  if (activeUser?.role === 'student') {
    const studentSchoolId = activeUser.schoolId || activeUser.school;
    const ferpaGranted = typeof activeUser.ferpaConsent === 'object'
      ? activeUser.ferpaConsent?.granted === true
      : activeUser.ferpaConsent === true;

    const myAdvisors = advisors.filter(advisor => {
      if (!ferpaGranted) return false;

      const servedIds = advisor.schoolsServedIds || [];
      const servedLegacy = advisor.schoolsServed || [];
      return (
        advisor.schoolId === studentSchoolId ||
        advisor.school === studentSchoolId ||
        servedIds.includes(studentSchoolId) ||
        servedLegacy.includes(studentSchoolId)
      );
    });

    const myRecommendations = recommendations.filter(r => r.studentId === activeUser.uid);
    const recommendedOpps = myRecommendations.map(rec => {
       const opp = opportunities?.find(o => o.id === rec.opportunityId);
       return { rec, opp };
    }).filter((item): item is { rec: typeof item.rec; opp: NonNullable<typeof item.opp> } => Boolean(item.opp));

    // Filter myAdvisors by search query
    const filteredMyAdvisors = myAdvisors.filter(advisor => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const nameMatch = (advisor.name || '').toLowerCase().includes(q);
      const emailMatch = (advisor.email || '').toLowerCase().includes(q);
      const schoolMatch = (resolveSchoolName(advisor.schoolId || advisor.school || '')).toLowerCase().includes(q);
      return nameMatch || emailMatch || schoolMatch;
    });

    // Filter recommendedOpps by search query
    const filteredRecommendedOpps = recommendedOpps.filter(({ rec, opp }) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const titleMatch = (opp.title || rec.opportunityTitle || '').toLowerCase().includes(q);
      const companyMatch = (opp.company || '').toLowerCase().includes(q);
      const advisorMatch = (rec.advisorName || '').toLowerCase().includes(q);
      return titleMatch || companyMatch || advisorMatch;
    });

    return (
      <div className="space-y-6 font-sans animate-fade-in" id="student-advisor-view">
        <HeroBanner 
          title="Advisors"
          subtitle="Connect with your academic advisor to review recommendations and pathway alignments."
          pageKey="advisors"
        />
        
        <div className="w-[95%] mx-auto space-y-8">
          {/* Full-width Search & Toolbar */}
          <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search advisors, schools, or recommended opportunities..."
            />
            <div className="flex items-center gap-2.5 shrink-0 self-end md:self-center">
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/70 rounded-lg font-semibold transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  title="Reset search filter"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>
              )}
              <div className="text-xs font-semibold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200/60 font-mono">
                Advisors: <strong className="text-slate-800">{filteredMyAdvisors.length}</strong> • Recommendations: <strong className="text-slate-800">{filteredRecommendedOpps.length}</strong>
              </div>
            </div>
          </div>

          {/* SECTION 1: Your Academic Advisors (Table View) */}
          <div className="space-y-3">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
              Your Academic Advisors
            </h3>

            {myAdvisors.length === 0 ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm flex flex-col items-center justify-center py-10 text-slate-500 text-center space-y-4">
                <div className="bg-slate-100 p-4 rounded-full">
                  <Shield className="w-6 h-6 text-slate-400" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">No Verified Advisor Connections</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed font-medium">
                    {!ferpaGranted
                      ? 'FERPA authorization is required before academic advisors can view your record and connect with you.'
                      : 'There are currently no academic advisors assigned to your high school campus.'}
                  </p>
                </div>

                {!ferpaGranted && (
                  <div className="w-full max-w-md pt-2 text-left">
                    <FerpaConsentReminderCard 
                      compact={true}
                      title="Send Parent FERPA Consent Email"
                      subtitle="Send an authorization reminder email to your parent or legal guardian."
                      buttonText="Send Email to Parent"
                    />
                  </div>
                )}
              </div>
            ) : filteredMyAdvisors.length === 0 ? (
              <div className="bg-white border border-slate-200/90 rounded-2xl py-12 text-center text-slate-400 font-medium shadow-xs">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-700">No advisors matching your search query</p>
                <p className="text-xs text-slate-400 mt-1">Try clearing your search term to see all assigned advisors.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[950px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            <span>Advisor Profile</span>
                          </span>
                        </th>
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                            <span>Designated Campus Access</span>
                          </span>
                        </th>
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                            <span>Status</span>
                          </span>
                        </th>
                        <th className="py-3 px-6 text-right font-mono">Advisor Actions & Profile</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredMyAdvisors.map((advisor) => {
                        const isExpanded = !!expandedAdvisorIds[advisor.uid];
                        const primarySchoolName = resolveSchoolName(advisor.schoolId || advisor.school || activeUser?.school || 'Assigned High School');
                        const advisorStudentRecs = myRecommendations.filter(r => r.advisorId === advisor.uid);

                        return (
                          <React.Fragment key={advisor.uid}>
                            <tr
                              onClick={() => toggleAdvisorDetails(advisor.uid)}
                              className="hover:bg-slate-50/50 transition-colors group cursor-pointer border-l-4 border-l-emerald-500"
                            >
                              {/* Column 1: Advisor Profile & Identity */}
                              <td className="py-4 px-6 align-middle">
                                <div className="flex items-center space-x-3.5">
                                  <div className="w-11 h-11 rounded-xl overflow-hidden border border-slate-200/90 bg-slate-50 shadow-2xs shrink-0 group-hover:border-[#0094c8]/50 transition-colors">
                                    <StudentAvatar 
                                      studentId={advisor.uid} 
                                      studentName={advisor.name} 
                                      profile={advisor} 
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <h4 className="font-extrabold text-slate-850 text-sm tracking-tight group-hover:text-[#0094c8] transition-colors truncate">
                                        {advisor.name}
                                      </h4>
                                      <span className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[8px] font-black uppercase tracking-wider shrink-0 border border-blue-100/80">
                                        Advisor
                                      </span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1 text-[11px] text-slate-500 font-mono">
                                      <span className="flex items-center gap-1 truncate">
                                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                                        {advisor.email}
                                      </span>
                                      {advisor.contactPhone && (
                                        <span className="flex items-center gap-1 shrink-0">
                                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                          {advisor.contactPhone}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Column 2: Campus Access */}
                              <td className="py-4 px-6 align-middle">
                                <div className="inline-flex items-center space-x-1.5 bg-slate-900 text-white rounded-lg px-2.5 py-1 text-[10px] font-bold shadow-2xs border border-slate-800">
                                  <GraduationCap className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                                  <span className="max-w-[200px] truncate">{primarySchoolName}</span>
                                </div>
                              </td>

                              {/* Column 3: Status Badge */}
                              <td className="py-4 px-6 align-middle">
                                <span className="rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 font-mono shadow-2xs">
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                  AUTHORIZED ADVISOR
                                </span>
                              </td>

                              {/* Column 4: Action Buttons */}
                              <td className="py-4 px-6 align-middle text-right whitespace-nowrap">
                                <div className="inline-flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                  {/* Direct Email Action Button */}
                                  <a
                                    href={`mailto:${advisor.email}?subject=WBL%20Student%20Inquiry`}
                                    className="w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs shrink-0 cursor-pointer"
                                    title="Send Email to Advisor"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </a>

                                  {/* View Advisor Profile Button */}
                                  <button
                                    type="button"
                                    onClick={() => toggleAdvisorDetails(advisor.uid)}
                                    className="w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs cursor-pointer active:scale-95 shrink-0"
                                    title={isExpanded ? "Collapse Advisor Profile" : "View Advisor Profile"}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>

                                  {/* Chevron Expand Button */}
                                  <button
                                    type="button"
                                    onClick={() => toggleAdvisorDetails(advisor.uid)}
                                    className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95 shrink-0"
                                    title={isExpanded ? "Collapse Details" : "Expand Details"}
                                  >
                                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {/* Expandable Advisor Profile Details */}
                            {isExpanded && (
                              <tr className="bg-slate-50/25 border-t border-slate-100">
                                <td colSpan={4} className="p-0">
                                  <div className="p-6 md:p-8 animate-in slide-in-from-top-2 duration-300">
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                      {/* Left Column: Bio, Campus Access & Recommendations */}
                                      <div className="lg:col-span-7 space-y-5">
                                        {/* Professional Bio / Description */}
                                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-2.5">
                                          <div className="flex items-center justify-between">
                                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                              <Sparkles className="w-3.5 h-3.5 text-[#0094c8]" />
                                              <span>Professional Background & Advising Focus</span>
                                            </h4>
                                            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                              ID: {advisor.uid.slice(0, 8)}...
                                            </span>
                                          </div>
                                          <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-100 text-xs text-slate-600 leading-relaxed">
                                            {advisor.description ? (
                                              <div 
                                                className="prose prose-sm max-w-none text-slate-600 [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-medium [&_a]:cursor-pointer"
                                                dangerouslySetInnerHTML={{ __html: advisor.description }}
                                              />
                                            ) : (
                                              <p className="italic text-slate-400">
                                                {advisor.name} serves as your academic and career pathway advisor, dedicated to reviewing your work-based learning progress, nominating you for opportunities, and providing career readiness guidance.
                                              </p>
                                            )}
                                          </div>
                                        </div>

                                        {/* Institutional Campus Authorizations */}
                                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3">
                                          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                            <Shield className="w-3.5 h-3.5 text-emerald-600" />
                                            <span>Campus Authorization & Clearance</span>
                                          </h4>
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-150/70">
                                              <span className="text-slate-400 font-bold block uppercase text-[9px] font-mono">Assigned Campus</span>
                                              <span className="text-slate-800 font-extrabold flex items-center gap-1 mt-0.5">
                                                <GraduationCap className="w-3.5 h-3.5 text-[#0094c8]" />
                                                {primarySchoolName}
                                              </span>
                                            </div>
                                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-150/70">
                                              <span className="text-slate-400 font-bold block uppercase text-[9px] font-mono">FERPA Disclosure Access</span>
                                              <span className="text-slate-800 font-extrabold flex items-center gap-1 mt-0.5">
                                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                                Verified & Authorized
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Opportunities Recommended to You */}
                                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3">
                                          <div className="flex items-center justify-between">
                                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                              <Star className="w-3.5 h-3.5 text-amber-500" />
                                              <span>Opportunities Recommended Specifically To You</span>
                                            </h4>
                                            <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                              {advisorStudentRecs.length} Recommendations
                                            </span>
                                          </div>

                                          {advisorStudentRecs.length > 0 ? (
                                            <div className="divide-y divide-slate-100 border border-slate-150 rounded-xl overflow-hidden">
                                              {advisorStudentRecs.map((rec) => (
                                                <div key={rec.id} className="p-3 bg-white hover:bg-slate-50/50 flex items-center justify-between gap-3 text-xs">
                                                  <div className="min-w-0">
                                                    <p className="font-bold text-slate-800 truncate">{rec.opportunityTitle}</p>
                                                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                                      Recommended on {rec.recommendedAt ? new Date(rec.recommendedAt).toLocaleDateString() : 'Recent'}
                                                    </p>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      sessionStorage.setItem('lastViewBeforeOpportunity', 'advisors');
                                                      setCurrentView('opportunities');
                                                    }}
                                                    className="px-3 py-1.5 bg-[#0094c8] hover:bg-[#007ba6] text-white text-[9px] font-black uppercase tracking-wider rounded-lg transition-all shadow-2xs shrink-0 cursor-pointer"
                                                  >
                                                    View
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="p-4 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center text-slate-400 text-xs">
                                              No opportunities recommended directly to your profile by this advisor yet.
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Right Column: Advisor Profile / Engagement Center (Appealing Large Picture & Card) */}
                                      <div className="lg:col-span-5 space-y-5">
                                        <div className="bg-gradient-to-br from-[#0f2d4a] via-[#133758] to-[#1e4d79] text-white rounded-3xl p-7 shadow-xl relative overflow-hidden border border-white/10 flex flex-col items-center text-center">
                                          {/* Background glowing accents */}
                                          <div className="absolute top-0 right-0 w-44 h-44 bg-[#0094c8]/30 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
                                          <div className="absolute bottom-0 left-0 w-36 h-36 bg-blue-500/10 rounded-full blur-2xl -ml-16 -mb-16 pointer-events-none" />

                                          <div className="relative z-10 w-full space-y-5 flex flex-col items-center">
                                            {/* Large Prominent Advisor Portrait */}
                                            <div className="relative group">
                                              <div className="absolute -inset-1.5 bg-gradient-to-r from-[#0094c8] to-emerald-400 rounded-3xl blur opacity-60 group-hover:opacity-100 transition duration-300"></div>
                                              <div className="relative w-28 h-28 rounded-3xl overflow-hidden border-3 border-white/30 shadow-2xl bg-slate-800">
                                                <StudentAvatar 
                                                  studentId={advisor.uid} 
                                                  studentName={advisor.name} 
                                                  profile={advisor} 
                                                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                                                />
                                              </div>
                                              <span className="absolute -bottom-2 -right-2 bg-emerald-500 text-white p-1.5 rounded-xl border-2 border-[#0f2d4a] shadow-md" title="Verified Active Advisor">
                                                <CheckCircle className="w-4 h-4 stroke-[3]" />
                                              </span>
                                            </div>

                                            {/* Advisor Name & Title */}
                                            <div className="space-y-1.5 w-full">
                                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0094c8]/30 text-sky-200 text-[10px] font-black uppercase tracking-wider border border-white/15">
                                                <Shield className="w-3 h-3 text-sky-300" />
                                                Certified Academic Advisor
                                              </span>
                                              <h3 className="text-xl font-black tracking-tight text-white uppercase">
                                                {advisor.name}
                                              </h3>
                                              <p className="text-xs text-sky-200 font-mono truncate px-2">{advisor.email}</p>
                                            </div>

                                            {/* Direct Contact Buttons */}
                                            <div className="w-full space-y-2 pt-1">
                                              <a
                                                href={`mailto:${advisor.email}?subject=WBL%20Student%20Coordination`}
                                                className="w-full py-2.5 px-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl text-xs font-bold text-white flex items-center justify-center gap-2.5 transition-all shadow-sm active:scale-98 cursor-pointer"
                                              >
                                                <Mail className="w-4 h-4 text-sky-300" />
                                                <span>Send Advisor Email</span>
                                              </a>
                                              {advisor.contactPhone && (
                                                <a
                                                  href={`tel:${advisor.contactPhone}`}
                                                  className="w-full py-2.5 px-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl text-xs font-bold text-white flex items-center justify-center gap-2.5 transition-all shadow-sm active:scale-98 cursor-pointer"
                                                >
                                                  <Phone className="w-4 h-4 text-emerald-300" />
                                                  <span>Call Direct Phone ({advisor.contactPhone})</span>
                                                </a>
                                              )}
                                            </div>

                                            {/* Quick Metrics Strip */}
                                            <div className="w-full grid grid-cols-2 gap-3 pt-2 border-t border-white/15 text-center">
                                              <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
                                                <span className="text-[9px] font-black text-sky-200 uppercase tracking-wider block font-mono">
                                                  Assigned Campus
                                                </span>
                                                <span className="text-xs font-extrabold text-white truncate block mt-0.5">
                                                  {primarySchoolName}
                                                </span>
                                              </div>
                                              <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
                                                <span className="text-[9px] font-black text-sky-200 uppercase tracking-wider block font-mono">
                                                  Nominations
                                                </span>
                                                <span className="text-lg font-black text-white mt-0.5 block">
                                                  {advisorStudentRecs.length}
                                                </span>
                                              </div>
                                            </div>

                                            {/* Action link */}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                sessionStorage.setItem('lastViewBeforeOpportunity', 'advisors');
                                                setCurrentView('opportunities');
                                              }}
                                              className="w-full py-3 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg cursor-pointer active:scale-98"
                                            >
                                              <span>Explore Recommended Opportunities</span>
                                              <ExternalLink className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 2: Advisor Recommendations (Table View mimicking Applications/Placements) */}
          <div className="space-y-3 pt-4 border-t border-slate-200/80">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
              Advisor Recommendations
            </h3>

            {recommendedOpps.length === 0 ? (
              <div className="bg-white border border-slate-200/90 rounded-2xl p-12 flex flex-col items-center justify-center text-center shadow-xs">
                <Star className="w-8 h-8 text-slate-300 mb-2" />
                <h4 className="text-sm font-black text-slate-700 uppercase tracking-tight mb-1">No Recommendations Found</h4>
                <p className="text-xs text-slate-500 max-w-md">Your advisors have not recommended any work-based learning opportunities to your profile yet.</p>
              </div>
            ) : filteredRecommendedOpps.length === 0 ? (
              <div className="bg-white border border-slate-200/90 rounded-2xl py-12 text-center text-slate-400 font-medium shadow-xs">
                <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-700">No recommended opportunities matching your search</p>
                <p className="text-xs text-slate-400 mt-1">Try clearing your search term to see all recommendations.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[950px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                            <span>Opportunity & Host Details</span>
                          </span>
                        </th>
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <Star className="w-3.5 h-3.5 text-slate-400" />
                            <span>Recommending Advisor & Date</span>
                          </span>
                        </th>
                        <th className="py-3 px-6">
                          <span className="inline-flex items-center gap-1.5 uppercase">
                            <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                            <span>Status</span>
                          </span>
                        </th>
                        <th className="py-3 px-6 text-right font-mono">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredRecommendedOpps.map(({ rec, opp }) => {
                        const isExpanded = !!expandedRecIds[rec.id];

                        return (
                          <React.Fragment key={rec.id}>
                            <tr
                              onClick={() => setExpandedRecIds(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                              className="hover:bg-slate-50/50 transition-colors group cursor-pointer border-l-4 border-l-[#0094c8]"
                            >
                              {/* Column 1: Opportunity & Host */}
                              <td className="py-4 px-6 align-middle">
                                <div className="flex items-center space-x-3.5">
                                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#0094c8] border border-blue-100 flex items-center justify-center shrink-0 group-hover:bg-[#0094c8] group-hover:text-white transition-colors duration-200 shadow-2xs">
                                    <Star className="w-5 h-5 fill-current" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="font-extrabold text-slate-850 text-sm tracking-tight group-hover:text-[#0094c8] transition-colors uppercase truncate">
                                      {opp.title || rec.opportunityTitle}
                                    </h4>
                                    <div className="flex items-center gap-2 text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                                      <span className="font-bold text-slate-600 uppercase">{opp.company || 'Host Organization'}</span>
                                      {opp.industry && (
                                        <>
                                          <span className="text-slate-300">•</span>
                                          <span className="text-slate-400 uppercase text-[10px]">{opp.industry}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Column 2: Recommending Advisor */}
                              <td className="py-4 px-6 align-middle">
                                <div>
                                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                    <User className="w-3.5 h-3.5 text-[#0094c8]" />
                                    <span>{rec.advisorName || 'Academic Advisor'}</span>
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                    Recommended: {rec.recommendedAt ? new Date(rec.recommendedAt).toLocaleDateString() : 'Active'}
                                  </div>
                                </div>
                              </td>

                              {/* Column 3: Status Badge */}
                              <td className="py-4 px-6 align-middle">
                                <span className="rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 border border-sky-300 bg-sky-50 text-sky-700 font-mono shadow-2xs">
                                  <Star className="w-3.5 h-3.5 text-sky-600 fill-current" />
                                  RECOMMENDED
                                </span>
                              </td>

                              {/* Column 4: Actions */}
                              <td className="py-4 px-6 align-middle text-right whitespace-nowrap">
                                <div className="inline-flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                  {/* View Opportunity Button */}
                                  <button 
                                    type="button"
                                    onClick={() => {
                                      sessionStorage.setItem('lastViewBeforeOpportunity', 'advisors');
                                      setCurrentView('opportunities');
                                    }}
                                    className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white text-[10px] font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-2xs shrink-0 active:scale-95"
                                  >
                                    View Opportunity
                                  </button>

                                  {/* Details toggle button */}
                                  <button
                                    type="button"
                                    onClick={() => setExpandedRecIds(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs cursor-pointer active:scale-95 shrink-0"
                                    title={isExpanded ? "Close Opportunity Overview" : "View Opportunity Overview"}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>

                                  {/* Chevron expand */}
                                  <button
                                    type="button"
                                    onClick={() => setExpandedRecIds(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95 shrink-0"
                                    title={isExpanded ? "Collapse Details" : "Expand Details"}
                                  >
                                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {/* Expandable Recommended Opportunity Details */}
                            {isExpanded && (
                              <tr className="bg-slate-50/25 border-t border-slate-100">
                                <td colSpan={4} className="p-0">
                                  <div className="p-6 md:p-8 animate-in slide-in-from-top-2 duration-300">
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                      <div className="lg:col-span-8 space-y-4">
                                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-2">
                                          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                            <Briefcase className="w-3.5 h-3.5 text-[#0094c8]" />
                                            <span>Opportunity Overview & Description</span>
                                          </h4>
                                          <p className="text-xs text-slate-600 leading-relaxed">
                                            {opp.description || 'No detailed position overview provided.'}
                                          </p>
                                        </div>

                                        {opp.requirements && (
                                          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-2">
                                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                                              Requirements & Qualifications
                                            </h4>
                                            <p className="text-xs text-slate-600 leading-relaxed">
                                              {opp.requirements}
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="lg:col-span-4 space-y-4">
                                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3 text-xs">
                                          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                                            Quick Specs
                                          </h4>
                                          <div className="space-y-2 text-[11px] font-mono">
                                            {opp.location && (
                                              <div className="flex justify-between py-1 border-b border-slate-100">
                                                <span className="text-slate-400 uppercase">Location:</span>
                                                <span className="font-bold text-slate-700">{opp.location}</span>
                                              </div>
                                            )}
                                            {opp.type && (
                                              <div className="flex justify-between py-1 border-b border-slate-100">
                                                <span className="text-slate-400 uppercase">Work Type:</span>
                                                <span className="font-bold text-slate-700">{opp.type}</span>
                                              </div>
                                            )}
                                            {(opp.compensation || opp.hourlyWage) && (
                                              <div className="flex justify-between py-1 border-b border-slate-100">
                                                <span className="text-slate-400 uppercase">Compensation:</span>
                                                <span className="font-bold text-emerald-600">{opp.compensation || `$${opp.hourlyWage}/hr`}</span>
                                              </div>
                                            )}
                                          </div>

                                          <button
                                            type="button"
                                            onClick={() => {
                                              sessionStorage.setItem('lastViewBeforeOpportunity', 'advisors');
                                              setCurrentView('opportunities');
                                            }}
                                            className="w-full mt-2 py-2.5 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-black uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer"
                                          >
                                            <span>Open in Opportunities</span>
                                            <ExternalLink className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-sans animate-fade-in" id="advisors-page-container">
      {/* Hero Header Area */}
      <HeroBanner 
        title="Advisors"
        subtitle="Attach academic advisors to schools to authorize their information access to active students and signed FERPA disclosures."
        pageKey="advisors"
      >
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs font-medium text-white/80 mt-2">
          <div className="flex items-center space-x-1.5 bg-white/10 px-2.5 py-1 rounded-lg border border-white/15">
            <Shield className="w-3.5 h-3.5 text-blue-400" />
            <span>Region Administrator Portal</span>
          </div>
        </div>
      </HeroBanner>

      <div className="w-[95%] mx-auto space-y-6">
                {/* Alerts and notifications */}
        {saveSuccess && (
          <div className="bg-emerald-900 border border-emerald-500/30 text-emerald-100 rounded-xl py-3.5 px-4 flex items-center space-x-2.5 text-xs font-bold shadow-md animate-fade-in">
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{saveSuccess}</span>
          </div>
        )}

        {errorMsg && (
          <div className="bg-red-950 border border-red-500/30 text-red-100 rounded-xl py-3.5 px-4 flex items-center space-x-2.5 text-xs font-bold shadow-md animate-fade-in">
            <X className="w-4 h-4 text-red-400 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Filters and Search toolbar */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-3">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search advisors by name, email, or campus access..."
          />
          <div className="flex items-center gap-2.5 shrink-0 self-end md:self-center">
            {(searchQuery || statusFilter !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
                className="px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/70 rounded-lg font-semibold transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                title="Reset all filters"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset</span>
              </button>
            )}
            <div className="text-xs font-semibold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200/60 font-mono">
              Advisors: <strong className="text-slate-800">{filteredAdvisors.length}</strong> / {advisors.length}
            </div>
          </div>
        </div>

        {/* Advisors Directory List */}
        {filteredAdvisors.length === 0 ? (
          <div className="bg-white border border-slate-200/90 rounded-2xl py-16 text-center text-slate-400 font-medium shadow-xs">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-700">No advisors matching search parameters</p>
            <p className="text-xs text-slate-400 mt-1">Try refining your search keyword or clearing the status filter.</p>
            {(searchQuery || statusFilter !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
                className="mt-4 px-4 py-2 bg-[#0094c8] text-white text-xs font-bold rounded-xl shadow-xs hover:bg-[#007ba6] transition-colors cursor-pointer"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[950px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                    {/* Column 1: Advisor Profile */}
                    <th className="py-3 px-6">
                      <button
                        type="button"
                        onClick={() => setSortBy(sortBy === 'name_asc' ? 'name_desc' : 'name_asc')}
                        className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                        title="Sort by Advisor Name"
                      >
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span>Advisor Profile</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortBy === 'name_asc' || sortBy === 'name_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                      </button>
                    </th>

                    {/* Column 2: Campus Access */}
                    <th className="py-3 px-6">
                      <button
                        type="button"
                        onClick={() => setSortBy(sortBy === 'school' ? 'name_asc' : 'school')}
                        className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                        title="Sort by Campus Access"
                      >
                        <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                        <span>Designated Campus Access</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortBy === 'school' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                      </button>
                    </th>

                    {/* Column 3: Status with Filter Funnel */}
                    <th className="py-3 px-6 relative">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSortBy(sortBy === 'status' ? 'name_asc' : 'status')}
                          className="group inline-flex items-center gap-1 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                          title="Sort by Access Status"
                        >
                          <span>Status</span>
                          <ArrowUpDown className={`w-3 h-3 ${sortBy === 'status' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                        </button>
                        
                        <div className="relative" ref={statusDropdownRef}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusDropdownOpen(!statusDropdownOpen);
                            }}
                            className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                              statusFilter !== 'all'
                                ? 'bg-[#0094c8] text-white shadow-2xs'
                                : 'bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200'
                            }`}
                            title="Filter by Status"
                          >
                            <Filter className="w-3.5 h-3.5" />
                            {statusFilter !== 'all' && (
                              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                            )}
                          </button>

                          {statusDropdownOpen && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal normal-case text-slate-800"
                            >
                              <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100 flex items-center justify-between">
                                <span>Filter Status</span>
                                {statusFilter !== 'all' && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setStatusFilter('all');
                                      setStatusDropdownOpen(false);
                                    }}
                                    className="text-[#0094c8] hover:underline cursor-pointer lowercase font-sans text-[10px]"
                                  >
                                    clear
                                  </button>
                                )}
                              </div>
                              {[
                                { key: 'all' as const, label: 'All Advisors', count: advisorCounts.total },
                                { key: 'authorized' as const, label: 'Authorized Access', count: advisorCounts.authorized },
                                { key: 'unassigned' as const, label: 'Pending Assignment', count: advisorCounts.unassigned },
                              ].map((opt) => (
                                <button
                                  key={opt.key}
                                  type="button"
                                  onClick={() => {
                                    setStatusFilter(opt.key);
                                    setStatusDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-slate-50 cursor-pointer font-medium ${
                                    statusFilter === opt.key ? 'text-[#0094c8] bg-sky-50/50 font-bold' : 'text-slate-700'
                                  }`}
                                >
                                  <span>{opt.label}</span>
                                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                                    {opt.count}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </th>

                    {/* Column 4: Assignment Workflow & Actions */}
                    <th className="py-3 px-6 text-right font-mono">Assignment Workflow</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filteredAdvisors.map((advisor) => {
                    const assignedSchools = getAdvisorAssignedSchools(advisor);
                    const isAuthorized = assignedSchools.length > 0;
                    const isExpanded = !!expandedAdvisorIds[advisor.uid];

                    // Find remaining schools to offer in dropdown
                    const adminRegions = activeUser?.role === 'regional_admin' 
                      ? [activeUser.tenantId, ...(activeUser.extendedRegions || [])].filter(Boolean)
                      : [];
                      
                    const unassignedSchools = (allSchools || []).filter(s => {
                      if (activeUser?.role === 'regional_admin') {
                        if (!adminRegions.includes(s.regionId)) return false;
                        if (activeTenant?.id && activeTenant.id !== 'global' && s.regionId !== activeTenant.id) return false;
                      }
                      return !assignedSchools.some(assigned => assigned.name === s.name);
                    });

                    // Student recommendations submitted by this advisor
                    const advisorRecs = (recommendations || []).filter(
                      r => r.advisorId === advisor.uid || r.advisorEmail === advisor.email
                    );

                    const canManageSchools = ['superadmin', 'regional_admin'].includes(activeUser?.role || '');

                    return (
                      <React.Fragment key={advisor.uid}>
                        <tr 
                          onClick={() => toggleAdvisorDetails(advisor.uid)}
                          className={`hover:bg-slate-50/50 transition-colors group cursor-pointer ${
                            isAuthorized ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-amber-400'
                          }`}
                        >
                          {/* Column 1: Advisor Identity & Contact */}
                          <td className="py-4 px-6 align-middle">
                            <div className="flex items-center space-x-3.5">
                              <div className="w-11 h-11 rounded-xl overflow-hidden border border-slate-200/90 bg-slate-50 shadow-2xs shrink-0 group-hover:border-[#0094c8]/50 transition-colors">
                                <StudentAvatar 
                                  studentId={advisor.uid} 
                                  studentName={advisor.name} 
                                  profile={advisor} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center space-x-2">
                                  <h4 className="font-extrabold text-slate-850 text-sm tracking-tight group-hover:text-[#0094c8] transition-colors truncate">
                                    {advisor.name}
                                  </h4>
                                  <span className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[8px] font-black uppercase tracking-wider shrink-0 border border-blue-100/80">
                                    Advisor
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1 text-[11px] text-slate-500 font-mono">
                                  <span className="flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                                    {advisor.email}
                                  </span>
                                  {advisor.contactPhone && (
                                    <span className="flex items-center gap-1 shrink-0">
                                      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                      {advisor.contactPhone}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Column 2: Designated Campus Access */}
                          <td className="py-4 px-6 align-middle">
                            {assignedSchools.length === 0 ? (
                              <div className="space-y-1">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200/70 text-amber-700 text-[10px] font-bold">
                                  <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                                  <span>No Campuses Authorized</span>
                                </div>
                                {advisor.signupSchool && (
                                  <div className="flex items-center space-x-1.5 text-[10px] text-slate-500 font-medium">
                                    <Info className="w-3 h-3 text-sky-500 shrink-0" />
                                    <span>Requested: <strong className="text-slate-700">{advisor.signupSchool}</strong></span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {assignedSchools.map((schoolItem, idx) => (
                                  <div 
                                    key={idx}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center space-x-1.5 bg-slate-900 text-white rounded-lg pl-2.5 pr-1 py-1 text-[10px] font-bold shadow-2xs border border-slate-800 hover:bg-slate-800 transition-colors group/pill"
                                  >
                                    <GraduationCap className="w-3 h-3 text-sky-400 shrink-0" />
                                    <span className="max-w-[160px] truncate">{schoolItem.name}</span>
                                    {canManageSchools && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDetachSchool(advisor.uid, schoolItem.ref);
                                        }}
                                        className="p-0.5 hover:bg-red-600 rounded-md text-slate-400 hover:text-white transition-colors cursor-pointer"
                                        title={`Detach ${schoolItem.name}`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>

                          {/* Column 3: Status Badge */}
                          <td className="py-4 px-6 align-middle">
                            <div className="inline-flex items-center">
                              {isAuthorized ? (
                                <span className="rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 font-mono shadow-2xs">
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                  AUTHORIZED
                                </span>
                              ) : (
                                <span className="rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-700 font-mono shadow-2xs">
                                  <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                  UNASSIGNED
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Column 4: Workflow Controls & Icon Actions */}
                          <td className="py-4 px-6 align-middle text-right whitespace-nowrap">
                            <div className="inline-flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              {canManageSchools && (
                                <div className="flex items-center space-x-1.5">
                                  <select
                                    value={selectedSchoolForAdvisor[advisor.uid] || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setSelectedSchoolForAdvisor(prev => ({
                                        ...prev,
                                        [advisor.uid]: val
                                      }));
                                    }}
                                    className="bg-slate-50 hover:bg-slate-100/70 border border-slate-200 rounded-xl px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-tight focus:bg-white focus:ring-1 focus:ring-[#0094c8] outline-none max-w-[170px]"
                                  >
                                    <option value="">-- ATTACH SCHOOL --</option>
                                    {unassignedSchools.map(s => (
                                      <option key={s.id} value={s.id}>
                                        {s.name}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => handleAttachSchool(advisor.uid)}
                                    disabled={!selectedSchoolForAdvisor[advisor.uid]}
                                    className="bg-[#0094c8] hover:bg-[#007ba6] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl flex items-center space-x-1 cursor-pointer shadow-2xs transition-all shrink-0 disabled:cursor-not-allowed"
                                    title="Attach Campus Access"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span>Attach</span>
                                  </button>
                                </div>
                              )}

                              {/* Document / Profile Toggle Button */}
                              <button
                                type="button"
                                onClick={() => toggleAdvisorDetails(advisor.uid)}
                                className="w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs cursor-pointer active:scale-95 shrink-0"
                                title={isExpanded ? "Collapse Advisor Profile" : "View Advisor Profile"}
                              >
                                <FileText className="w-4 h-4" />
                              </button>

                              {/* Chevron Expand / Collapse Button */}
                              <button
                                type="button"
                                onClick={() => toggleAdvisorDetails(advisor.uid)}
                                className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs active:scale-95 shrink-0"
                                title={isExpanded ? "Collapse Details" : "Expand Details"}
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expandable Advisor Profile Details */}
                        {isExpanded && (
                          <tr className="bg-slate-50/25 border-t border-slate-100">
                            <td colSpan={4} className="p-0">
                              <div className="p-6 md:p-8 animate-in slide-in-from-top-2 duration-300">
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                  {/* Left Column: Bio, Campus Access & Recommendations */}
                                  <div className="lg:col-span-7 space-y-5">
                                    {/* Professional Bio / Description */}
                                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-2.5">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                          <Sparkles className="w-3.5 h-3.5 text-[#0094c8]" />
                                          <span>Professional Background & Advising Focus</span>
                                        </h4>
                                        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                          ID: {advisor.uid.slice(0, 8)}...
                                        </span>
                                      </div>
                                      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-100 text-xs text-slate-600 leading-relaxed">
                                        {advisor.description ? (
                                          <div 
                                            className="prose prose-sm max-w-none text-slate-600 [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-medium [&_a]:cursor-pointer"
                                            dangerouslySetInnerHTML={{ __html: advisor.description }}
                                          />
                                        ) : (
                                          <p className="italic text-slate-400">
                                            {advisor.name} serves as an academic and career pathway advisor, guiding students through work-based learning opportunities, industry credentials, and career readiness certifications.
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Institutional Campus Authorizations */}
                                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3">
                                      <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                        <Shield className="w-3.5 h-3.5 text-emerald-600" />
                                        <span>Institutional Access & FERPA Clearance</span>
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                                        <div className="p-3 rounded-xl bg-slate-50 border border-slate-150/70">
                                          <span className="text-slate-400 font-bold block uppercase text-[9px] font-mono">Authorization Level</span>
                                          <span className="text-slate-800 font-extrabold flex items-center gap-1 mt-0.5">
                                            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                            {isAuthorized ? 'Authorized High School Staff' : 'Pending Administrative Clearance'}
                                          </span>
                                        </div>
                                        <div className="p-3 rounded-xl bg-slate-50 border border-slate-150/70">
                                          <span className="text-slate-400 font-bold block uppercase text-[9px] font-mono">Student FERPA Records</span>
                                          <span className="text-slate-800 font-extrabold flex items-center gap-1 mt-0.5">
                                            <Award className="w-3.5 h-3.5 text-sky-500" />
                                            {isAuthorized ? 'Read Access for Designated Campuses' : 'Restricted until Assigned'}
                                          </span>
                                        </div>
                                      </div>

                                      <div>
                                        <span className="text-slate-400 font-bold block uppercase text-[9px] font-mono mb-2">Authorized Campuses</span>
                                        {assignedSchools.length > 0 ? (
                                          <div className="flex flex-wrap gap-2">
                                            {assignedSchools.map((item, idx) => (
                                              <span 
                                                key={idx}
                                                className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold inline-flex items-center gap-1.5"
                                              >
                                                <GraduationCap className="w-3.5 h-3.5 text-sky-600" />
                                                {item.name}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-amber-600 italic">No campuses currently attached to this advisor record.</p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Active Student Recommendations */}
                                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono flex items-center gap-1.5">
                                          <Star className="w-3.5 h-3.5 text-amber-500" />
                                          <span>Student Recommendations & Nominations</span>
                                        </h4>
                                        <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                          {advisorRecs.length} Total
                                        </span>
                                      </div>

                                      {advisorRecs.length > 0 ? (
                                        <div className="divide-y divide-slate-100 border border-slate-150 rounded-xl overflow-hidden">
                                          {advisorRecs.slice(0, 4).map((rec) => (
                                            <div key={rec.id} className="p-3 bg-white hover:bg-slate-50/50 flex items-center justify-between gap-3 text-xs">
                                              <div className="min-w-0">
                                                <p className="font-bold text-slate-800 truncate">{rec.studentName}</p>
                                                <p className="text-[11px] text-[#0094c8] font-medium truncate">{rec.opportunityTitle}</p>
                                              </div>
                                              <div className="text-right shrink-0">
                                                <span className="text-[9px] font-mono uppercase text-slate-400 block">
                                                  {rec.recommendedAt ? new Date(rec.recommendedAt).toLocaleDateString() : 'Active'}
                                                </span>
                                                <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[9px] font-extrabold uppercase font-mono border border-emerald-200">
                                                  Nominated
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="p-4 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center text-slate-400 text-xs">
                                          No student recommendations recorded for this advisor account yet.
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right Column: Advisor Profile / Engagement Center */}
                                  <div className="lg:col-span-5 space-y-5">
                                    <div className="bg-gradient-to-br from-[#0f2d4a] via-[#133758] to-[#1e4d79] text-white rounded-2xl p-6 shadow-md relative overflow-hidden">
                                      {/* Background accent */}
                                      <div className="absolute top-0 right-0 w-36 h-36 bg-[#0094c8]/20 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />

                                      <div className="relative z-10 space-y-5">
                                        <div className="flex items-start gap-4">
                                          <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/20 shadow-md bg-white/10 shrink-0">
                                            <StudentAvatar 
                                              studentId={advisor.uid} 
                                              studentName={advisor.name} 
                                              profile={advisor} 
                                              className="w-full h-full object-cover"
                                            />
                                          </div>
                                          <div className="min-w-0">
                                            <span className="inline-block px-2 py-0.5 rounded-md bg-[#0094c8]/30 text-sky-200 text-[9px] font-black uppercase tracking-wider mb-1 border border-white/10">
                                              Academic Advisor
                                            </span>
                                            <h3 className="text-lg font-black tracking-tight text-white uppercase truncate">
                                              {advisor.name}
                                            </h3>
                                            <p className="text-xs text-sky-150 font-mono truncate">{advisor.email}</p>
                                          </div>
                                        </div>

                                        {/* Direct Contact Buttons */}
                                        <div className="space-y-2 pt-1">
                                          <a
                                            href={`mailto:${advisor.email}?subject=WBL%20Student%20Coordination`}
                                            className="w-full py-2 px-3 bg-white/10 hover:bg-white/20 border border-white/15 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-2 transition-colors shadow-2xs"
                                          >
                                            <Mail className="w-3.5 h-3.5 text-sky-300" />
                                            <span>Send Advisor Email</span>
                                          </a>
                                          {advisor.contactPhone && (
                                            <a
                                              href={`tel:${advisor.contactPhone}`}
                                              className="w-full py-2 px-3 bg-white/10 hover:bg-white/20 border border-white/15 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-2 transition-colors shadow-2xs"
                                            >
                                              <Phone className="w-3.5 h-3.5 text-emerald-300" />
                                              <span>Call Direct Phone</span>
                                            </a>
                                          )}
                                        </div>

                                        {/* Quick Metrics Strip */}
                                        <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-white/10 text-center">
                                          <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
                                            <span className="text-[9px] font-black text-sky-200 uppercase tracking-wider block font-mono">
                                              Campuses
                                            </span>
                                            <span className="text-base font-black text-white">
                                              {assignedSchools.length}
                                            </span>
                                          </div>
                                          <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
                                            <span className="text-[9px] font-black text-sky-200 uppercase tracking-wider block font-mono">
                                              Nominations
                                            </span>
                                            <span className="text-base font-black text-white">
                                              {advisorRecs.length}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Action link */}
                                        <button
                                          type="button"
                                          onClick={() => setCurrentView('applications')}
                                          className="w-full py-2.5 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-black uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer"
                                        >
                                          <span>View Student Applications</span>
                                          <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
